import { transport } from '@opengov/app-core/transport';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FOOTER_1, FULL_WIDTH_1, HEADER_1 } from '@opengov/stories-shared/constants';
import { IStoryContent } from '@opengov/stories-shared/types';
import {
  fetchProgressTrackerSettings,
  generateBase64HTML,
  orderStoryContents,
  selectNewStoryContents,
  sortStoryContents
} from '@opengov/stories-shared/utils';
import { ISettings } from '../../../../types';
import { applyBrandingSettings, purgeUrlCache } from '../../../utils';
import { storiesSettingsQueryKeys } from './storiesSettingsQueryKeys';

export type GeneratePreviewParams = {
  settings: ISettings;
  entitySubdomain: string;
  storyMarginsEnabled: boolean;
};

export type UseGeneratePreviewOptions = {
  onSuccess?: (previewUrl: string) => void;
};

// Module-level counter preserves the race-condition guard from the original
// effect: only the last in-flight call updates the preview URL.
let generatePreviewCallCount = 0;

export function useGeneratePreview({ entityId, onSuccess: onSuccessCallback }: { entityId: number } & UseGeneratePreviewOptions) {
  const queryClient = useQueryClient();

  const { mutate: generatePreview, isLoading: isPreviewLoading } = useMutation({
    mutationFn: async ({ settings, entitySubdomain, storyMarginsEnabled }: GeneratePreviewParams): Promise<string | null> => {
      const newStoryContents = selectNewStoryContents();
      const fonts = settings.brand.fonts;
      const filteredContents = newStoryContents.filter(
        (c) => c.format === HEADER_1 || c.format === FULL_WIDTH_1 || c.format === FOOTER_1
      ) as unknown as IStoryContent[];
      const storyContents = orderStoryContents(sortStoryContents(filteredContents));
      const styledStoryContents = storyContents.map((storyContent) =>
        applyBrandingSettings(storyContent, settings, '')
      );

      generatePreviewCallCount++;

      const progressTrackerSettings = await fetchProgressTrackerSettings(settings.entity_id);

      const html = generateBase64HTML({
        storyContents: styledStoryContents,
        title: 'preview',
        description: 'story preview',
        hasGallery: false,
        hasFootNotes: false,
        fonts,
        entityId: settings.entity_id,
        progressTrackerSettings
      });

      const buffer: { image: string } = await transport
        .url('/api/content_export_service/v1/image')
        .json({
          showMargins: !storyMarginsEnabled,
          htmlEncoded: html,
          fullPage: true
        })
        .post()
        .json();

      const thumbnailData: { screenshot: string } = await transport
        .url('/api/stories/v1/stories/s3/thumbnail')
        .json({
          _id: settings.entity_id,
          buffer: buffer.image,
          key: [`${entitySubdomain}`, 'preview']
        })
        .post()
        .json();

      generatePreviewCallCount--;

      if (generatePreviewCallCount === 0) {
        return purgeUrlCache(thumbnailData.screenshot);
      }

      return null;
    },

    onSuccess: (previewUrl) => {
      if (previewUrl === null) return;

      queryClient.setQueryData(
        storiesSettingsQueryKeys.settings(entityId),
        (old: ISettings | undefined) => {
          if (!old) return old;
          return { ...old, previewUrl };
        }
      );
      onSuccessCallback?.(previewUrl);
    },

    onError: () => {
      generatePreviewCallCount = Math.max(0, generatePreviewCallCount - 1);
    }
  });

  return { generatePreview, isPreviewLoading };
}
